import { DynamicFormRef } from "@mavichpe/dynamic-forms";
import { useEffect, useRef, useState } from "react";
import QuoteFormConfig from "./form";
import styles from "./style.module.scss";
import { QuoteFormData, QuoteRelatedAttributes } from "@/formTypes/QuoteDataType";
import GenericForm, { ConfirmationConfig } from "../GenericForm";
import { QuoteApi, QuoteDataType } from "@/api/QuoteApi";
import QuickAddProductForm from "../QuickAddProductForm";
import { Col, Row } from "reactstrap";
import PriceSelector from "@components/dynamic/PriceSelector";
import { PriceApi } from "@/api/PricesApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectEDocumentSummary, selectEDocumentVentaNeta } from "@/store/slices/utils/eDocumentSelectors";
import { FOUR_PRICES_MANAGEMENT_CODE, CREDIT_NOTE_DISCOUNT_TYPE_CODE, ON_INVOICE_DISCOUNT_TYPE_CODE } from "@utils/Constants";
import { clearDocument, loadProducts, setDocumentOtherCharges, setDocumentShippingOptions, setAllProductsExemptionData, setDefaultExemption, setDocumentComments, setDocumentDiscount, setDocumentDiscountCode, setDocumentDiscountType, setDocumentExempted, setPrice, setPriceCode, setProductExemptionData } from "@/store/slices/eDocumentSlides";
import { readClientShippingOptions } from "@utils/ClientShipping";
import { CurrencyApi } from "@/api/CurrencyApi";
import { setCurrency } from "@/store/slices/currencySlides";
import LoadingScreen from "@components/content/LoadingScreen";
import EdocumentProductList from "@components/dynamic/EdocumentProductList";
import { ClientApi, ClientDataType } from "@/api/ClientApi";
import { showAlert } from "@utils/Alert";
import { isNaN } from "mathjs";
import { generateNextNumberByModel, omitProperties, removeFunctionFromObject, stripHtmlTags } from "@utils/Utils";
import { AddClientToDocumentPubSub, AddClientToDocumentPubSubPayload, ConfirmPrintPubSub, CurrencyIdPubSub, LoadInvoiceTemplatePubSub, LoadInvoiceTemplatePubSubPayload, ScrollToDiscountCodeSelectPubSub } from "@utils/pubsubs/PubSub";
import { flushDocumentProductEdits } from "@utils/eDocumentFlush";
import EdocumentComments from "@components/dynamic/EdocumentComments";
import DocumentExemptionOffCanvas from "@components/layout/Offcanvas/DocumentExemptionOffCanvas";
import { ProductExemptionDataType } from "@/formTypes/ProductExemptionDataType";
import { generateCustomInformationJson } from "@utils/EDocument";
import { QUOTE_APPROVAL_IN_REVIEW } from "@utils/quoteApprovalStatus";
import {
  parseStoredOtherCharges,
  resolveOtherCharges,
  serializeStoredOtherCharges,
} from "@utils/DocumentOtherCharges";
import CustomDocumentFields from "@components/dynamic/CustomDocumentFields";
import { getCurrentDateTime } from "@utils/Api";
import { QuoteProductDataType } from "@/api/QuoteProductApi";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { QuoteFormDataKeys } from "@/generated/QuoteFormDataKeys";
import DocumentPrintConfirmation from "@components/content/DocumentPrintConfirmation";
import SearchClientByIdentificationOffCanvas from "@components/layout/Offcanvas/SearchClientByIdentificationOffCanvas";
import { loadReferenceFormLabel } from "@utils/Forms";
import { ActivityCodeApi } from "@/api/ActivityCodeApi";
import { buildDocumentCopyFormValues, endDocumentPreload, resolveDocumentClientNames, resolveDocumentDiscountToPersist, resolveStoredDocumentDiscount } from "@utils/documentClientSnapshot";
import { DOCUMENT_LIMIT_REACHED_MESSAGE, currentMonthPrefix, getDocumentLimit } from "@/security/documentLimits.catalog";

type QuoteFormProps = {
  id?: string;
  copyData?: boolean
  formId: string,
  saveRedirectUrl: string
}


const QuoteForm = (props: QuoteFormProps) => {
  const dispatch = useAppDispatch()
  const { copyData = false, id: preLoadedQuoteId, saveRedirectUrl } = props
  const formRef = useRef<DynamicFormRef>(null);
  const customFieldForm = useRef<DynamicFormRef>(null);
  const isPreloadingDocumentRef = useRef(false);

  const [defaultData, setDefaultData] = useState<QuoteDataType>({});
  const [clients, setClients] = useState<ClientDataType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const suscriptionData = useAppSelector((state) => state.suscription.suscription);
  const planDocumentLimits = useAppSelector((state) => state.suscription.planDocumentLimits);
  const documentCurrency = useAppSelector((state) => state.currency);
  const [formConfig, setFormConfig] = useState<any>();

  const suscriptionId = suscriptionData?.id

  useEffect(() => {
    setIsLoading(true)
    loadFormConfig()
    loadDefaultData()
  }, [suscriptionId])

  useEffect(() => {
    const unsubscribe = AddClientToDocumentPubSub.subscribe(addClientToDocument);
    const unsubscribeInvoiceTemplate = LoadInvoiceTemplatePubSub.subscribe(loadInvoiceTemplate);
    dispatch(clearDocument())
    return () => {
      unsubscribe();
      unsubscribeInvoiceTemplate();
    };
  }, []);

  const loadFormConfig = () => {
    const formConfig = loadReferenceFormLabel(QuoteFormConfig, suscriptionData);
    if (suscriptionData?.id) {
      setFormConfig(formConfig)
    }
  }

  const loadInvoiceTemplate = (payload: LoadInvoiceTemplatePubSubPayload) => {
    const { invoiceTemplate } = payload;

    if (invoiceTemplate?.id) {
      const defaultFormData: QuoteDataType = {
        currencyId: invoiceTemplate.currencyId,
        exenta: invoiceTemplate.exenta
      }
      formRef.current?.setFormValue("multiple", defaultFormData, "multipleData")
    }
    dispatch(setDocumentDiscount(Number(invoiceTemplate?.porcenta_descuento || 0)))
    dispatch(setDocumentExempted(invoiceTemplate?.exenta || false))
    dispatch(loadProducts(invoiceTemplate?.invoiceTemplateProducts as EDocumentProductDataType[]))
  }

  const addClientToDocument = (payload: AddClientToDocumentPubSubPayload) => {
    const { client } = payload;
    setClients([
      ...clients,
      client
    ])
    populateClientData(client)
    formRef.current?.reFetchSelectInput("clientId")
  }

  const onInputUpdate = (name: keyof QuoteFormData, value: any, event?: React.ChangeEvent<any>) => {
    if (name === "clientId") {
      if (isPreloadingDocumentRef.current) {
        return;
      }
      const client = clients.find(c => c.id === value);
      if (client?.id) {
        populateClientData(client);
      }
    }
  }

  const setClientPrice = async (client: ClientDataType) => {
    if (suscriptionData?.pricemanaged === FOUR_PRICES_MANAGEMENT_CODE) {
      let priceCode = Number(client.priceId || 1);
      if (isNaN(priceCode)) {
        priceCode = 1
      }
      dispatch(setPriceCode(priceCode))
    } else {
      if (typeof client.price == "function") {
        const { data } = await client.price()
        let priceData = removeFunctionFromObject(data)
        dispatch(setPrice(priceData))
      }
    }
  }

  const populateClientData = async (client: ClientDataType) => {
    if (client?.id) {

      const clientData: QuoteDataType = {
        clientId: client.id,
        ...resolveDocumentClientNames(client),
        tel1: client.telefono1,
        tel1_cd: client.telefono1_cd,
        tel2: client.telefono2,
        tel2_cd: client.telefono2_cd,
        email: client.email,
        tipo_identificacion: client.tipo_identificacion,
        identificacion: client.identificacion,
        direccion: stripHtmlTags(client.direccion || ""),
        clientActivitycode: client.activitycode,
      }

      formRef.current?.setFormValue("multiple",
        {
          ...clientData,
          client_code: client.codigo
        }
        , "multipleData");
      let descuento = Number(client.descuento || 0);
      if (isNaN(descuento)) {
        descuento = 0;
      }

      dispatch(setDocumentDiscount(descuento))
      dispatch(setDocumentDiscountType(client.tipo_descuento || ON_INVOICE_DISCOUNT_TYPE_CODE))
      dispatch(setDocumentShippingOptions(readClientShippingOptions(client)))
      setClientPrice(clientData)
    }
  }

  const onInputKeyDown = (name: keyof QuoteFormData | "client_code", event?: React.KeyboardEvent<any>) => {
    if (name === "client_code") {
      if (event?.key == "Enter") {
        event?.preventDefault();
        event?.stopPropagation();
        const input = event.target as HTMLInputElement
        selectClientByCode(input.value)
      }
    }
  }

  const selectClientByCode = async (code: string) => {
    const client = clients.find(c => c.codigo === code);
    if (client?.id) {
      formRef.current?.setFormValue("clientId", client.id, "String");
    } else {
      showAlert("danger", "No existe ningun cliente con ese codigo");
    }
  }

  const onAddExemption = (exemptionChange?: ProductExemptionDataType) => {
    if (exemptionChange?.productId) {
      dispatch(setProductExemptionData({
        productId: `${exemptionChange?.productId}`,
        exemption: exemptionChange
      }))
    } else if (exemptionChange) {
      dispatch(setAllProductsExemptionData({
        exemption: exemptionChange
      }))
      dispatch(setDefaultExemption(exemptionChange))
    }
  }

  const preLoadQuoteData = async (clients: ClientDataType[]) => {
    if (preLoadedQuoteId) {
      let quote = await QuoteApi.get(preLoadedQuoteId, {
        selectionSet: [
          ...QuoteFormDataKeys.filter(k => !QuoteRelatedAttributes.includes(k)),
          "currency.*",
          "client.*",
          "quoteProducts.*",
          "quoteProducts.product.*"
        ]
      })

      // Antes de `loadProducts`: `setDocumentDiscount` sobrescribe el
      // `porcentaje_descuento` de cada linea y `setDocumentDiscountType` decide si el
      // impuesto de cada una se calcula con el descuento restado.
      const storedDiscount = resolveStoredDocumentDiscount(quote);
      dispatch(setDocumentDiscountType(storedDiscount.documentDiscountType))
      dispatch(setDocumentDiscount(storedDiscount.documentDiscount))
      if (storedDiscount.documentDiscountCode) {
        dispatch(setDocumentDiscountCode(storedDiscount.documentDiscountCode))
      }
      dispatch(setDocumentExempted(storedDiscount.isDocumentExempted))

      let formData = omitProperties(quote, ["createdAt", "updatedAt"], true);

      if (copyData) {
        formData = omitProperties(quote, ["numero", "id"], true);
      }

      const client = clients.find(c => c.id == formData.clientId);
      isPreloadingDocumentRef.current = true;
      formRef.current?.resetFormValue(
        buildDocumentCopyFormValues(formData, client)
      );
      endDocumentPreload(isPreloadingDocumentRef);
      dispatch(setDocumentComments(`${formData.comments || ""}`))
      dispatch(setDocumentOtherCharges(parseStoredOtherCharges(quote.otros_cargos)))
      dispatch(setDocumentShippingOptions(client ? readClientShippingOptions(client) : []))

      if (!copyData && client?.id) {
        await setClientPrice(client as ClientDataType);
      }

      if (formData.customfields) {
        try {
          const customFields = JSON.parse(formData.customfields);
          const fieldData = customFields.reduce((a: Record<string, string>, v: { title: string, value: string, }) => {
            return {
              ...a,
              [v.title]: v.value
            }
          }, {})
          customFieldForm.current?.resetFormValue(fieldData)
        } catch (err) {
          console.log("error parsing json")
        }
      }

      if (quote.quoteProducts) {
        const quoteProducts = quote.quoteProducts.map(ip => {
          let product = ip;
          if (copyData) {
            product = omitProperties(product, ["id", "quoteId"]);
          } else {
            product.preSavedId = product.id
          }
          return omitProperties(ip, ["quoteId", "createdAt", "updatedAt"], true);
        })
        dispatch(loadProducts(quoteProducts as EDocumentProductDataType[]))
      }
      if (quote.currency && quote.currencyId) {
        CurrencyIdPubSub.publish({ currencyId: quote.currencyId })
      }
    }
  }

  const setDocumentPriceCode = (priceData: any) => {
    if (typeof priceData === 'number') {
      dispatch(setPriceCode(priceData))
    } else {
      let formatterPrice = removeFunctionFromObject(priceData)
      if (formatterPrice.formula) {
        dispatch(setPrice(formatterPrice))
      } else {
        dispatch(setPriceCode(1))
      }
    }
  }

  const loadDefaultData = async (resetLoad = false) => {
    try {
      const { items: allClients } = await ClientApi.fetchAll();
      setClients(allClients);

      if (!resetLoad && preLoadedQuoteId) {
        await preLoadQuoteData(allClients)
      } else {
        dispatch(clearDocument())
        const defaultPrice = await PriceApi.getDefaultPrice()
        setDocumentPriceCode(defaultPrice)

        if (typeof defaultPrice === 'number') {
          dispatch(setPriceCode(defaultPrice))
        } else {
          let priceData = removeFunctionFromObject(defaultPrice)
          dispatch(setPrice(priceData))
        }
        const defaultCurrency = await CurrencyApi.getDefaultCurrency()
        if (defaultCurrency.id) {
          dispatch(setCurrency(removeFunctionFromObject(defaultCurrency)))
          defaultData["currencyId"] = defaultCurrency.id
          if (defaultCurrency.exchange_rate) {
            defaultData["exchange_rate"] = defaultCurrency.exchange_rate
          }
          CurrencyIdPubSub.publish({ currencyId: defaultCurrency.id })
        }

        const defaultActivityCode = await ActivityCodeApi.getDefaultActivityCode()
        if (defaultActivityCode.code) {
          defaultData["activitycode"] = `${defaultActivityCode.code}`
        }

        formRef.current?.setFormValue("multiple", defaultData, "multipleData")
        setDefaultData(defaultData)
        if (resetLoad) {
          formRef.current?.resetFormValue(defaultData)
          customFieldForm.current?.resetFormValue()
        }
        const defaultClient = (allClients as ClientDataType[]).find(c => c.is_default);
        if (defaultClient && defaultClient.id) {
          setTimeout(() => {
            formRef.current?.setFormValue("clientId", defaultClient.id, "select");
          }, 200);
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Tope mensual de cotizaciones del plan. Las cotizaciones no pasan por save-edocument,
  // así que la validación se hace en el frontend antes de crear.
  const assertQuoteLimitAvailable = async () => {
    const limit = getDocumentLimit(planDocumentLimits, "Quote");
    if (limit <= 0) return; // plan sin límite de cotizaciones
    const count = await QuoteApi.countByMonth(currentMonthPrefix());
    if (count >= limit) {
      throw Error(DOCUMENT_LIMIT_REACHED_MESSAGE);
    }
  }

  const createQuote = async (formData: QuoteDataType): Promise<QuoteDataType> => {
    const eDocumentState = flushDocumentProductEdits();
    const documentSummaryState = selectEDocumentSummary(eDocumentState);
    let quoteData: QuoteDataType = {}
    const {
      documentProducts,
      documentDiscountType,
      isDocumentExempted,
      documentDiscountCode,
    } = eDocumentState;

    const discountToPersist = resolveDocumentDiscountToPersist(
      documentDiscountType,
      documentSummaryState.descuento,
      documentDiscountCode,
    )
    if (discountToPersist.requiresDiscountCode) {
      ScrollToDiscountCodeSelectPubSub.publish({})
      throw Error("Por favor seleccione un codigo de descuento antes de continuar")
    }
    quoteData["discountCode"] = discountToPersist.discountCode

    if (documentProducts?.length == 0) {
      throw Error("Por favor agregue algun producto a la cotizacion")
    }

    const customField = await customFieldForm.current?.getFormData()

    if (eDocumentState.hasAdditionalData) {
      if (!customField) {
        throw Error("Por favor ingrese la informacion addicional que es obligatoria")
      } else {
        quoteData["customfields"] = generateCustomInformationJson(customField)
      }
    }

    quoteData = {
      ...formData,
      ...quoteData,
      exchange_rate: formData.exchange_rate || 1,
      subtotal: documentSummaryState.subtotal,
      descuento: documentDiscountType == ON_INVOICE_DISCOUNT_TYPE_CODE ? documentSummaryState.descuento : 0,
      impuestos: documentSummaryState.impuestos,
      total: documentSummaryState.total,
      currencyId: documentCurrency.currencyApiId,
      descuento_con_nota: documentDiscountType == CREDIT_NOTE_DISCOUNT_TYPE_CODE,
      fecha: getCurrentDateTime(),
      exenta: isDocumentExempted,
      porcenta_descuento: eDocumentState.documentDiscount,
      comments: eDocumentState.documentComments,
      // Resueltos contra la venta neta final (un cargo porcentual definido antes de
      // agregar lineas guarda un `montoCargo` viejo). Incondicional porque al duplicar
      // `formData` arrastra el `otros_cargos` del origen.
      otros_cargos: serializeStoredOtherCharges(
        resolveOtherCharges(eDocumentState.documentOtherCharges, selectEDocumentVentaNeta(eDocumentState)),
      ),
      // Toda cotizacion nace pendiente de revision (aplique o no el flujo de aprobacion).
      approval_status: QUOTE_APPROVAL_IN_REVIEW,
    }

    if (documentProducts) {
      await assertQuoteLimitAvailable()
      const createdQuote = await QuoteApi.createQuoteAndProducts(quoteData, documentProducts as QuoteProductDataType[])
      clearForm()
      showAlert("success", "La Cotizacion ha sido creada existosamente")
      ConfirmPrintPubSub.publish({
        openOffCanvas: true,
        printVersionUrl: `/user/quotes/print/${createdQuote.id}/0/1`,
      })
      return createdQuote;
    }

    return {} as QuoteDataType;
  }

  const updateQuote = async (quoteId: string, formData: QuoteDataType): Promise<QuoteDataType> => {
    const eDocumentState = flushDocumentProductEdits();
    const documentSummaryState = selectEDocumentSummary(eDocumentState);
    let quoteData: QuoteDataType = {}
    const { documentProducts, documentDiscountType, isDocumentExempted, documentDiscountCode } = eDocumentState;

    if (documentProducts?.length == 0) {
      throw Error("Por favor agregue algun producto a la cotizacion")
    }

    // Mismo criterio que al crear: `discountCode` no es un campo del formulario (vive en el
    // slice `eDocument`), asi que si no se asigna aqui el update no lo manda y el codigo
    // elegido se pierde.
    const discountToPersist = resolveDocumentDiscountToPersist(
      documentDiscountType,
      documentSummaryState.descuento,
      documentDiscountCode,
    )
    if (discountToPersist.requiresDiscountCode) {
      ScrollToDiscountCodeSelectPubSub.publish({})
      throw Error("Por favor seleccione un codigo de descuento antes de continuar")
    }
    quoteData["discountCode"] = discountToPersist.discountCode

    const customField = await customFieldForm.current?.getFormData()

    if (eDocumentState.hasAdditionalData) {
      if (!customField) {
        throw Error("Por favor ingrese la informacion addicional que es obligatoria")
      } else {
        quoteData["customfields"] = generateCustomInformationJson(customField)
      }
    }

    quoteData = {
      ...formData,
      ...quoteData,
      subtotal: documentSummaryState.subtotal,
      descuento: documentDiscountType == ON_INVOICE_DISCOUNT_TYPE_CODE ? documentSummaryState.descuento : 0,
      impuestos: documentSummaryState.impuestos,
      total: documentSummaryState.total,
      currencyId: documentCurrency.currencyApiId,
      descuento_con_nota: documentDiscountType == CREDIT_NOTE_DISCOUNT_TYPE_CODE,
      fecha: getCurrentDateTime(),
      exenta: isDocumentExempted,
      porcenta_descuento: eDocumentState.documentDiscount,
      comments: eDocumentState.documentComments,
      // Resueltos contra la venta neta final (un cargo porcentual definido antes de
      // agregar lineas guarda un `montoCargo` viejo). Incondicional porque al duplicar
      // `formData` arrastra el `otros_cargos` del origen.
      otros_cargos: serializeStoredOtherCharges(
        resolveOtherCharges(eDocumentState.documentOtherCharges, selectEDocumentVentaNeta(eDocumentState)),
      ),
      // Editar el documento invalida cualquier aprobacion previa: vuelve a revision.
      approval_status: QUOTE_APPROVAL_IN_REVIEW,
    }

    if (documentProducts) {
      const createdQuote = await QuoteApi.updateQuoteAndProducts(quoteData, documentProducts as QuoteProductDataType[])
      clearForm()
      showAlert("success", "La Cotizacion ha sido Actualizada existosamente")
      return createdQuote;
    }

    return {} as QuoteDataType;
  }

  const clearForm = async () => {
    dispatch(clearDocument())
    loadDefaultData(true)
  }

  const getCreateConfirmation = (): ConfirmationConfig | undefined => {
    const { ask_confirm = false } = suscriptionData || {}
    if (ask_confirm) {
      return {
        message: "Esta seguro que desea crear la Cotizacion"
      }
    }
  }

  const onClientSelected = (clientData?: ClientDataType) => {
    const clientInvoiceData: QuoteDataType = {
      clientActivitycode: clientData?.activitycode,
      ...resolveDocumentClientNames(clientData || {}),
      identificacion: clientData?.identificacion,
      tipo_identificacion: clientData?.tipo_identificacion
    }
    formRef.current?.setFormValue("multiple", clientInvoiceData, "multipleData")
  }

  return (
    <>
      {formConfig?.sections && (<GenericForm<QuoteFormData, QuoteDataType>
        ref={formRef}
        copyData={copyData}
        formId={props.formId}
        defaultData={defaultData}
        modelRelativeObjects={QuoteRelatedAttributes}
        onInputChange={onInputUpdate}
        onInputKeyDown={onInputKeyDown}
        saveRedirectUrl={saveRedirectUrl}
        confirmationConfig={getCreateConfirmation()}
        formConfig={formConfig}
        getById={(...arg) => QuoteApi.get(...arg)}
        createFn={(...arg) => createQuote(...arg)}
        updateFn={(id, ...rest) => updateQuote(id, ...rest)}
      >
        <CustomDocumentFields ref={customFieldForm} documentType="quote" />
        <Row>
          <Col>
            <h3>
              Productos
            </h3>
          </Col>
          <Col xs="1">
            <PriceSelector />
          </Col>
        </Row>
        <QuickAddProductForm />
        <EdocumentProductList showOtherCharges={true} />
        <EdocumentComments />
      </GenericForm >
      )}
      <DocumentExemptionOffCanvas onAddExemption={onAddExemption} />
      <LoadingScreen isLoading={isLoading} />
      <DocumentPrintConfirmation
        title="Imprimir Cotizacion"
      />
      <SearchClientByIdentificationOffCanvas
        onClientSelected={onClientSelected}
      />
    </>

  )
}

export default QuoteForm;